import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { authService } from '@/services/auth/authService';
import type { HierarchyNodeDto } from '@/components/forms/TreeSelectPicker';

/**
 * Minimal adjacency-list shape a self-referencing entity must expose for tree presentation
 * (DSL `lookup.presentation: tree`). `code`/`isActive` are optional in the source DTO.
 */
export interface AdjacencyItem {
  id: string;
  name: string;
  code?: string | null;
  parentId?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

/**
 * The one sibling-order rule every tree in the app follows: `sortOrder` first, name as tiebreaker.
 *
 * The tiebreaker is what keeps legacy data (every sortOrder still 0) in a stable alphabetical order
 * until someone assigns values, and it must be identical everywhere — the org tree, the rollup tree
 * and the tree pickers all render the same data, so a divergent copy shows the same group in two
 * different orders.
 */
export function compareTreeSiblings(
  a: { sortOrder?: number; name: string },
  b: { sortOrder?: number; name: string },
): number {
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name);
}

/**
 * Each node's position in the tree read depth-first, siblings by {@link compareTreeSiblings} — the order the tree
 * pickers show the nodes in, for a list of nodes that must follow it. A node whose parent is not in the list is a root.
 */
export function treePositions(nodes: { id: string; name: string; parentId?: string | null; sortOrder?: number }[]): Map<string, number> {
  const ids = new Set(nodes.map((n) => n.id));
  const children = new Map<string | null, typeof nodes>();
  for (const node of nodes) {
    const parent = node.parentId && ids.has(node.parentId) ? node.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), node]);
  }
  const positions = new Map<string, number>();
  const visit = (parent: string | null) => {
    for (const node of [...(children.get(parent) ?? [])].sort(compareTreeSiblings)) {
      if (positions.has(node.id)) continue;
      positions.set(node.id, positions.size);
      visit(node.id);
    }
  };
  visit(null);
  return positions;
}

/**
 * Project a flat adjacency list onto the hierarchy-node shape TreeSelectPicker renders,
 * deriving `depth` (parentId walk) and `hasChildren` (any item names this as parent)
 * client-side — lookup entities like OrgUnit have no `/tree` endpoint, so the tree is
 * assembled here from the adjacency list.
 */
export function adjacencyToHierarchyNodes(items: AdjacencyItem[]): HierarchyNodeDto[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  const childCount = new Map<string, number>();
  for (const item of items) {
    if (item.parentId) childCount.set(item.parentId, (childCount.get(item.parentId) ?? 0) + 1);
  }
  const depthOf = (item: AdjacencyItem): number => {
    let depth = 0;
    let cur: AdjacencyItem | undefined = item;
    while (cur?.parentId && byId.has(cur.parentId)) {
      depth += 1;
      cur = byId.get(cur.parentId);
    }
    return depth;
  };
  return items.map((item) => ({
    id: item.id,
    code: item.code ?? '',
    name: item.name,
    parentId: item.parentId ?? null,
    // Pass the source's sibling order through — TreeSelectPicker sorts each sibling group by
    // (sortOrder, name), so a hard-coded 0 here would silently fall back to alphabetical.
    sortOrder: item.sortOrder ?? 0,
    isActive: item.isActive ?? true,
    depth: depthOf(item),
    hasChildren: (childCount.get(item.id) ?? 0) > 0,
  }));
}

/**
 * Fetch a full entity list from an explicit endpoint and project it to hierarchy nodes.
 * Pass `null` to disable the fetch (returns an empty node list) — for pickers that only
 * sometimes show a tree (e.g. PrincipalPicker's org-node branch).
 */
export function useTreeLookupNodesFromUrl(url: string | null): HierarchyNodeDto[] {
  const tenantId = authService.getTenantId();
  const { data } = useQuery<AdjacencyItem[]>({
    queryKey: ['tree-lookup', url, tenantId],
    enabled: !!url,
    queryFn: async () => {
      const response = await apiClient.get(url!, { params: { maxResultCount: 1000 } });
      return response.data.items ?? response.data ?? [];
    },
  });
  return useMemo(() => adjacencyToHierarchyNodes(data ?? []), [data]);
}

/**
 * Fetch the full entity list for a tree-presented lookup and project it to hierarchy nodes.
 * Uses the same URL convention as the lookup autocomplete endpoint
 * (`/api/{module}/{entity}/lookup`), minus the `/lookup` suffix — i.e. the entity's
 * standard list endpoint.
 */
export function useTreeLookupNodes(module: string, entity: string): HierarchyNodeDto[] {
  return useTreeLookupNodesFromUrl(`/api/${module.toLowerCase()}/${entity.toLowerCase()}`);
}
